import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../utils/api';
import './ChangePassword.css';

function ChangePassword() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Check authentication
  useEffect(() => {
    if (!ApiService.isAuthenticated()) {
      navigate('/');
      return;
    }

    // Get user data
    const userData = ApiService.getCurrentUser();
    setUser(userData);
    setIsLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    // Clear authentication data
    ApiService.logout();
    
    // Add a small delay to ensure state is cleared properly
    setTimeout(() => {
      // Force redirect to login page
      window.location.href = '/';
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Semua field harus diisi.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Password baru dan konfirmasi password tidak cocok.');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password baru minimal 8 karakter.');
      return;
    }
    
    // Check password complexity
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword);
    
    if (!hasUpperCase || !hasNumber || !hasSpecialChar) {
      setError('Password tidak memenuhi persyaratan kompleksitas.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      // Call API to change password
      await ApiService.changePassword(currentPassword, newPassword);
      setSuccess('Password telah berhasil diubah!');
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Redirect after success
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      setError(error.message || 'Gagal mengubah password. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="change-password-page">
      <header className="dashboard-header">
        <div className="container header-container">
          <div className="logo">
            <img src="/logo.png" alt="Company Logo" />
            <h1>Sistem Penggajian</h1>
          </div>
          <nav className="main-nav">
            <ul>
              <li><a href="/dashboard">Dashboard</a></li>
              <li><a href="/salary">Slip Gaji</a></li>
              <li className="active"><a href="/change-password">Ubah Password</a></li>
              {user && user.isAdmin && (
                <li><a href="/admin">Admin Panel</a></li>
              )}
            </ul>
          </nav>
          <div className="user-menu">
            <span className="username">{user ? user.name : 'User'}</span>
            <button 
              className="logout-btn"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="change-password-content">
        <div className="container">
          <div className="password-form-container">
            <h2>Ubah Password</h2>
            
            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="currentPassword">Password Saat Ini</label>
                <div className="password-container">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    id="currentPassword"
                    className="form-control"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                  <button 
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    disabled={isSubmitting}
                  >
                    {showCurrentPassword ? '🔒' : '👁️'}
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">Password Baru</label>
                <div className="password-container">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                  <button 
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={isSubmitting}
                  >
                    {showNewPassword ? '🔒' : '👁️'}
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Konfirmasi Password Baru</label>
                <div className="password-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                  <button 
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isSubmitting}
                  >
                    {showConfirmPassword ? '🔒' : '👁️'}
                  </button>
                </div>
              </div>
              
              <div className="password-requirements">
                <p>Persyaratan Password:</p>
                <ul>
                  <li className={newPassword.length >= 8 ? 'requirement-met' : ''}>
                    Minimal 8 karakter
                  </li>
                  <li className={/[A-Z]/.test(newPassword) ? 'requirement-met' : ''}>
                    Berisi minimal 1 huruf besar
                  </li>
                  <li className={/\d/.test(newPassword) ? 'requirement-met' : ''}>
                    Berisi minimal 1 angka
                  </li>
                  <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword) ? 'requirement-met' : ''}>
                    Berisi minimal 1 karakter khusus
                  </li>
                </ul>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="button" 
                  className="btn btn-cancel" 
                  onClick={() => navigate('/dashboard')}
                  disabled={isSubmitting}
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  className="btn btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Memproses...' : 'Ubah Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <footer className="dashboard-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} Sistem Penggajian. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default ChangePassword; 